/**
 * Gears — renders a chain of interlocking gears or pulleys.
 *
 * data: {
 *   gears: [
 *     { label: "A", teeth: 20, direction: "cw" },
 *     { label: "B", teeth: 40 },
 *     { label: "C", teeth: 15 },
 *   ],
 *   type: "gears" | "pulleys" | "belt"
 * }
 *
 * direction is auto-calculated from first gear (alternating for gears, same for belt pulleys)
 * teeth determines relative size
 * type: "gears" = interlocking, "pulleys"/"belt" = connected by belt (same direction)
 * showSolution: true = show all direction arrows (for explication), false = only first gear arrow
 */

function GearSvg({ cx, cy, r, teeth, direction, label, color, showArrow }) {
    const toothCount = Math.max(6, Math.min(teeth || 12, 60));
    const toothDepth = Math.max(3, r * 0.15);
    const innerR = r - toothDepth;
    const outerR = r;

    // Generate gear teeth path
    const points = [];
    for (let i = 0; i < toothCount; i++) {
        const a1 = (i / toothCount) * Math.PI * 2;
        const a2 = ((i + 0.35) / toothCount) * Math.PI * 2;
        const a3 = ((i + 0.5) / toothCount) * Math.PI * 2;
        const a4 = ((i + 0.85) / toothCount) * Math.PI * 2;
        points.push(`${cx + innerR * Math.cos(a1)},${cy + innerR * Math.sin(a1)}`);
        points.push(`${cx + outerR * Math.cos(a2)},${cy + outerR * Math.sin(a2)}`);
        points.push(`${cx + outerR * Math.cos(a3)},${cy + outerR * Math.sin(a3)}`);
        points.push(`${cx + innerR * Math.cos(a4)},${cy + innerR * Math.sin(a4)}`);
    }

    // Direction arrow
    const arrowR = innerR * 0.55;
    const arrowAngle = direction === 'cw' ? -Math.PI / 4 : Math.PI / 4;
    const arrowTip = {
        x: cx + arrowR * Math.cos(arrowAngle),
        y: cy + arrowR * Math.sin(arrowAngle),
    };

    return (
        <g>
            <polygon points={points.join(' ')} className="qv-gears__tooth" style={{ fill: color }} />
            <circle cx={cx} cy={cy} r={innerR * 0.85} className="qv-gears__body" style={{ fill: color }} />
            {/* Direction arrow — only when showArrow */}
            {showArrow && (
                <path
                    d={`M ${cx} ${cy - arrowR * 0.6} A ${arrowR * 0.6} ${arrowR * 0.6} 0 0 ${direction === 'cw' ? 1 : 0} ${cx + (direction === 'cw' ? arrowR * 0.5 : -arrowR * 0.5)} ${cy}`}
                    className="qv-gears__arrow"
                    markerEnd="url(#gearArrowHead)"
                />
            )}
            {/* Label */}
            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
                className="qv-gears__label">{label}</text>
            {/* Teeth count */}
            {teeth && (
                <text x={cx} y={cy + r + 14} textAnchor="middle"
                    className="qv-gears__teeth-label">{teeth}d</text>
            )}
        </g>
    );
}

function PulleySvg({ cx, cy, r, label, direction, color, showArrow }) {
    const arrowR = r * 0.5;
    return (
        <g>
            <circle cx={cx} cy={cy} r={r} className="qv-gears__pulley" style={{ stroke: color }} />
            {showArrow && (
                <path
                    d={`M ${cx} ${cy - arrowR} A ${arrowR} ${arrowR} 0 0 ${direction === 'cw' ? 1 : 0} ${cx + (direction === 'cw' ? arrowR : -arrowR)} ${cy}`}
                    className="qv-gears__arrow"
                    markerEnd="url(#gearArrowHead)"
                />
            )}
            <text x={cx} y={cy + r + 14} textAnchor="middle"
                className="qv-gears__label">{label}</text>
        </g>
    );
}

const COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#3b82f6'];

export default function Gears({ data }) {
    const gears = data?.gears;
    if (!Array.isArray(gears) || gears.length === 0) return null;

    const type = data.type || 'gears';
    const isBelt = type === 'pulleys' || type === 'belt';
    const showSolution = data.showSolution === true;

    // Calculate sizes based on teeth (or equal for pulleys)
    const maxTeeth = Math.max(...gears.map(g => g.teeth || 12));
    const minR = 30, maxR = 55;

    const elements = gears.map((g, i) => {
        const teeth = g.teeth || 12;
        const r = isBelt ? 38 : minR + ((teeth / maxTeeth) * (maxR - minR));
        const firstDir = gears[0].direction || 'cw';
        let direction;
        if (isBelt) {
            direction = firstDir; // belt: all same direction
        } else {
            direction = i % 2 === 0 ? firstDir : (firstDir === 'cw' ? 'ccw' : 'cw');
        }
        return { ...g, r, direction, color: COLORS[i % COLORS.length] };
    });

    // Position gears horizontally
    const biggestR = Math.max(...elements.map(e => e.r));
    const centerY = biggestR + 15;
    let totalWidth = 0;
    const positions = [];
    elements.forEach((g, i) => {
        if (i === 0) {
            positions.push({ cx: g.r + 10, cy: centerY });
            totalWidth = g.r + 10;
        } else {
            const prev = elements[i - 1];
            const gap = isBelt ? prev.r + g.r + 20 : prev.r + g.r - 3;
            const cx = positions[i - 1].cx + gap;
            positions.push({ cx, cy: centerY });
            totalWidth = cx + g.r + 10;
        }
    });

    const svgH = centerY + biggestR + 30;

    return (
        <div className="qv-gears">
            <svg viewBox={`0 0 ${totalWidth} ${svgH}`} className="qv-gears__svg" style={{ maxWidth: totalWidth }}>
                <defs>
                    <marker id="gearArrowHead" viewBox="0 0 10 10" refX="9" refY="5"
                        markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#475569" />
                    </marker>
                </defs>

                {/* Belt lines for pulleys */}
                {isBelt && positions.slice(1).map((pos, i) => {
                    const prev = positions[i];
                    const prevR = elements[i].r;
                    const currR = elements[i + 1].r;
                    return (
                        <g key={`belt${i}`}>
                            <line x1={prev.cx} y1={prev.cy - prevR} x2={pos.cx} y2={pos.cy - currR}
                                className="qv-gears__belt" />
                            <line x1={prev.cx} y1={prev.cy + prevR} x2={pos.cx} y2={pos.cy + currR}
                                className="qv-gears__belt" />
                        </g>
                    );
                })}

                {elements.map((g, i) => {
                    const pos = positions[i];
                    const showArrow = showSolution || i === 0;
                    return isBelt ? (
                        <PulleySvg key={i} cx={pos.cx} cy={pos.cy} r={g.r}
                            label={g.label} direction={g.direction} color={g.color}
                            showArrow={showArrow} />
                    ) : (
                        <GearSvg key={i} cx={pos.cx} cy={pos.cy} r={g.r}
                            teeth={g.teeth} direction={g.direction} label={g.label}
                            color={g.color} showArrow={showArrow} />
                    );
                })}
            </svg>
        </div>
    );
}
